import { FC, useEffect, useRef, useState } from 'react'
import { CarouselMobileFilters } from '@obeta/components/lib/carousel/CarouselMobileFilters'
import { CarouselFilter, FilterSection } from '@obeta/models/lib/models/UI'
import styles from './Carousel.module.scss'
import { Typography } from '@mui/material'
import { useBreakpoints } from '@obeta/data/lib/hooks'
import { SecondaryIconButton } from '@obeta/components/index'
import { FilterIcon } from '@obeta/components/lib/icons'
import { MobileFilters } from '@obeta/components/lib/filters/Filters'
import { Dropdown2 } from '@obeta/components/lib/dropdown2/Dropdown2'
import { CarouselSkeleton } from '@obeta/components/lib/carousel-skeleton/CarouselSkeleton'
import { ProductsSwiper } from './ProductsSwiper'
import { useOnEnterViewport } from '@obeta/data/lib/hooks/useOnEnterViewPort'

type CarouselProps = {
  /**
   * Function to render given element as a carousel card.
   * @param index Index
   * @returns Element for current index
   */
  cardRenderer: (index: number) => JSX.Element
  /**
   * Displays skeletons when isLoading is set to true.
   */
  isLoading?: boolean
  /**
   * Count of all rendered items.
   */
  itemCount: number
  /**
   * If set displays mobile filter dialog or desktop dropdowns.
   */
  filters?: CarouselFilter[]
  /**
   * CSS class for the skeletons loading state.
   */
  skeletonClassName?: string
  /**
   * Title of the carousel. Always displayed even if isLoading is set to true.
   */
  title: string
  /**
   * Gets called when cancel button is clicked in mobile filter dialog.
   * @returns void
   */
  onCancelMobileFilters?: () => void
  /**
   * Changes the filter value for given filter id.
   * @param id Filter id
   * @param value Filter value
   * @returns void
   */
  onChangeFilter?: (id: string, value: string | number) => void
  /**
   * Gets called when the carousel gets in the browser viewport (e.g. used for Datadog tracking).
   * @returns void
   */
  onEnterViewport?: () => void
  /**
   * Gets called when the carousel items get rendered the first time (e.g. used for Datadog tracking).
   * @returns void
   */
  onRenderItems?: () => void
}

/**
 * TODO: Move to components folder if circular dependency is fixed (ProductsSwiper)
 */
export const Carousel: FC<CarouselProps> = (props) => {
  const {
    cardRenderer,
    filters,
    isLoading,
    itemCount,
    skeletonClassName,
    title,
    onCancelMobileFilters,
    onChangeFilter,
    onEnterViewport,
    onRenderItems,
  } = props

  const { desktop, mobile, tablet } = useBreakpoints()

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState<boolean>(false)

  const mobileFilterSections: FilterSection[] =
    filters && filters?.length > 0
      ? filters?.map((filter) => ({
          id: filter.id,
          component: (
            <CarouselMobileFilters
              options={filter.options}
              value={filter.value}
              onChange={(value) => onChangeFilter && onChangeFilter(filter.id, value)}
            />
          ),
          title: filter.title,
        }))
      : []

  const containerRef = useRef<HTMLDivElement>(null)
  useOnEnterViewport(containerRef, onEnterViewport, itemCount)

  const renderItemsRef = useRef(onRenderItems)
  useEffect(() => {
    if (renderItemsRef.current && itemCount > 0) {
      renderItemsRef.current()
      renderItemsRef.current = undefined
    }
  }, [itemCount])

  return (
    <div className={styles.carousel}>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <Typography variant="headline3Bold">{title}</Typography>
          {filters && onCancelMobileFilters && (
            <div className={styles.filters}>
              {!desktop && (
                <>
                  <SecondaryIconButton
                    icon={<FilterIcon />}
                    onClick={() => setMobileFiltersOpen(true)}
                  />
                  <MobileFilters
                    amount={itemCount ?? 0}
                    open={mobileFiltersOpen}
                    sections={mobileFilterSections}
                    onClose={() => {
                      onCancelMobileFilters()
                      setMobileFiltersOpen(false)
                    }}
                    onSubmit={() => setMobileFiltersOpen(false)}
                  />
                </>
              )}
              {desktop &&
                filters.map((filter) => (
                  <Dropdown2
                    disabled={isLoading}
                    options={filter.options}
                    outerLabel={filter.title}
                    value={filter.value}
                    width="11.25rem"
                    onChange={(value) => onChangeFilter && onChangeFilter(filter.id, value)}
                  />
                ))}
            </div>
          )}
        </div>
        {isLoading ? (
          <CarouselSkeleton
            className={skeletonClassName}
            elementsPerRow={mobile ? 1 : tablet ? 2 : 4}
          />
        ) : (
          <ProductsSwiper
            cardWrapperClassName={styles.cardWrapper}
            withNavigateButtons={!mobile}
            elementsPerRow={mobile ? 'auto' : tablet ? 2 : 4}
            productElements={Array.from({ length: itemCount }).map((_, index) =>
              cardRenderer(index)
            )}
          />
        )}
      </div>
    </div>
  )
}
